import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKdsStore, sortedTickets } from '../../store/kdsStore';
import { useAuthStore } from '../../store/authStore';
import { TicketCard } from './TicketCard';
import { AllDayBoard } from './AllDayBoard';
import { ServeDialog } from './ServeDialog';
import {
  BoardFilters,
  EMPTY_FILTERS,
  filterTickets,
  isFiltering,
  useFilterLookups,
  type BoardFilterState,
} from './BoardFilters';
import { useI18n, setLanguage, type TKey } from '../../lib/i18n';
import { bandOf, bandsFor, elapsedSeconds, ORDER_TYPE_DELIVERY } from './helpers';
import { unlockAudio } from './sound';
import { useTheme, setTheme } from '../../lib/theme';
import type { KdsViewMode } from '../../types/kds';

// ─────────────────────────────────────────────────────────────────────────────
// The kitchen screen.
//
// Five views over one board, because a real restaurant needs all five and the
// legacy page offered none of them:
//
//   Station  what one prep station cooks. The default, and the one a cook
//            stares at all shift.
//   Expo     the pass: whole orders, per-station progress, SERVE.
//   All      every station on one screen. For a small kitchen with a single
//            display, and for a manager wanting the whole board at a glance.
//   All-day  item totals across the open board — "how many wings am I into".
//   Recall   what was bumped recently, so a mis-tap is one press from undone.
//
// Keyboard shortcuts exist because plenty of kitchens run these on a monitor
// with a cheap USB bump bar, which presents itself as a keyboard.
// ─────────────────────────────────────────────────────────────────────────────

const VIEWS: Array<{ mode: KdsViewMode; key: TKey }> = [
  { mode: 'station', key: 'station' },
  { mode: 'expo', key: 'expo' },
  { mode: 'all', key: 'all' },
  { mode: 'allday', key: 'allDay' },
  { mode: 'recall', key: 'recall' },
];

export interface KdsScreenProps {
  /** Switch to the legacy Service Manager grid. */
  onOpenServiceManager: () => void;
}

export function KdsScreen({ onOpenServiceManager }: KdsScreenProps): JSX.Element {
  const store = useKdsStore();
  const authState = useAuthStore((s) => s.state);
  const logout = useAuthStore((s) => s.logout);
  const { lang, dir, t } = useI18n();
  const theme = useTheme();

  const {
    ready,
    error,
    stations,
    settings,
    stationId,
    viewMode,
    tickets,
    conn,
    skewMs,
    pending,
    init,
    disconnect,
    selectStation,
    setViewMode,
    refresh,
  } = store;

  useEffect(() => {
    void init();
    return () => disconnect();
    // Mount only: init() reads its own state, and connect() rebinds on every
    // station/view change. Re-running this on identity changes would tear the
    // stream down and back up on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Coming back from a locked or sleeping tablet: the stream may have been
  // dropped by the OS without an error firing, so re-read the board on wake.
  // Cheap (one ETag'd request, usually a 304) and it removes the "screen was
  // showing breakfast at dinner service" failure entirely.
  useEffect(() => {
    const onVisible = (): void => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [refresh]);

  const station = useMemo(() => stations.find((s) => s.id === stationId), [stations, stationId]);
  const bands = useMemo(() => bandsFor(settings, station), [settings, station]);
  const stationById = useMemo(() => new Map(stations.map((s) => [s.id, s])), [stations]);

  // Board filters. Deliberately component state, not store state and never
  // persisted — see the note at the top of BoardFilters.tsx.
  const [filters, setFilters] = useState<BoardFilterState>(EMPTY_FILTERS);

  // The delivery order waiting on a driver decision, if any. Held by id rather
  // than by object so a ticket that changes under an open dialog (another
  // station bumps its share) does not freeze a stale copy on screen.
  const [serveTargetId, setServeTargetId] = useState<number | null>(null);
  const { options: filterOptions, itemCategories } = useFilterLookups();

  const allTickets = useMemo(() => sortedTickets(tickets), [tickets]);
  const list = useMemo(
    () => filterTickets(allTickets, filters, itemCategories),
    [allTickets, filters, itemCategories],
  );
  const hiddenCount = allTickets.length - list.length;
  const expoView = viewMode === 'expo' || (viewMode === 'station' && station?.type !== 'prep');

  // Does this board hold tickets from more than one station? True for All and
  // for Expo, and for Recall whenever no single station is selected. When it
  // is, every card is labelled with its station — otherwise one order's prep
  // and expo tickets are indistinguishable and look like a duplicate.
  const spansStations =
    viewMode === 'all' || viewMode === 'expo' || (viewMode === 'recall' && stationId == null);

  // ── keyboard / bump bar ───────────────────────────────────────────────────
  const listRef = useRef(list);
  listRef.current = list;

  /**
   * Serving a ticket, asking who took it when that question has an answer.
   *
   * A DELIVERY order opens the driver dialog; everything else serves on the one
   * tap it always did. The choice sits here rather than in TicketCard so the
   * card stays a memoised render of one ticket, and so the bump bar and the
   * button go through the same decision — a delivery handed over with the "1"
   * key must not skip the question the button asks.
   */
  const requestServe = useCallback(
    (ticketId: number): void => {
      const ticket = listRef.current.find((candidate) => candidate.id === ticketId);
      if (ticket && ticket.orderType === ORDER_TYPE_DELIVERY) {
        setServeTargetId(ticketId);
        return;
      }
      void store.serve(ticketId);
    },
    [store],
  );

  const serveTarget = serveTargetId == null ? null : tickets.get(serveTargetId) ?? null;

  // Read inside the keydown listener, which is bound once per view change and
  // must not be re-bound every time the dialog opens or closes.
  const dialogOpenRef = useRef(false);
  dialogOpenRef.current = serveTargetId != null;

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      // Never hijack typing in a field.
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      // The serve dialog owns the keyboard while it is open — otherwise "1"
      // would hand a different order over behind it.
      if (dialogOpenRef.current) return;

      const current = listRef.current;
      // 1-9 bump the nth card — what a USB bump bar sends, and also the
      // fastest thing for a cook with one free hand.
      if (/^[1-9]$/.test(event.key)) {
        const ticket = current[Number(event.key) - 1];
        if (ticket) {
          event.preventDefault();
          if (expoView) requestServe(ticket.id);
          else void store.bump(ticket.id);
        }
        return;
      }
      // Space / Enter bump the oldest — nearly always the one being finished.
      if (event.key === ' ' || event.key === 'Enter') {
        const oldest = current[0];
        if (oldest) {
          event.preventDefault();
          if (expoView) requestServe(oldest.id);
          else void store.bump(oldest.id);
        }
        return;
      }
      if (event.key === 'Backspace') {
        const last = current.find((ticket) => ticket.status === 'ready');
        if (last) {
          event.preventDefault();
          void store.recall(last.id);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expoView, requestServe, store]);

  if (authState.kind !== 'session') {
    return <div className="kds-empty">{t('loadingSession')}</div>;
  }

  const session = authState.data.session;
  const lateCount = list.filter(
    (ticket) =>
      ticket.status !== 'ready' &&
      bandOf(elapsedSeconds(ticket.firedAt, skewMs), bands.warn, bands.critical) === 'late',
  ).length;

  const title =
    viewMode === 'station'
      ? station?.name ?? t('kitchen')
      : t(VIEWS.find((v) => v.mode === viewMode)?.key ?? 'kitchen');

  return (
    <div className="kds-root" dir={dir} lang={lang} onPointerDown={unlockAudio}>
      <header className="kds-top">
        <div className="kds-top-left">
          <h1 className="kds-title">{title}</h1>
          <bdi className="kds-branch">
            {store.branchName ?? session.branch_name} · {session.username}
          </bdi>
        </div>

        <div className="kds-top-stats">
          <Stat label={t('open')} value={list.length} />
          <Stat label={t('late')} value={lateCount} tone={lateCount > 0 ? 'late' : undefined} />
          <ConnBadge conn={conn} />
          <DayPicker
            date={store.boardDate}
            today={store.serverToday}
            onPick={(d) => void store.setBoardDate(d)}
            t={t}
          />
        </div>

        <div className="kds-top-right">
          {/* One tap between English and Arabic. Deliberately not buried in a
              settings screen: the person who needs it is the cook standing at
              the station, and they will not go looking. */}
          <button
            type="button"
            className="kds-btn kds-btn-ghost"
            onClick={() => setLanguage(lang === 'ar' ? 'en' : 'ar')}
            title={lang === 'ar' ? 'English' : 'العربية'}
          >
            {lang === 'ar' ? 'EN' : 'ع'}
          </button>
          {/* Next to the language toggle because it is the same kind of thing:
              a per-screen preference the person standing there sets, not a
              setting an admin configures for a branch they cannot see. */}
          <button
            type="button"
            className="kds-btn kds-btn-ghost"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? t('lightMode') : t('darkMode')}
            aria-label={theme === 'dark' ? t('lightMode') : t('darkMode')}
          >
            {theme === 'dark' ? '☀' : '☾'}
          </button>
          <button
            type="button"
            className="kds-btn kds-btn-ghost"
            onClick={() => void refresh()}
            title={t('refresh')}
            aria-label={t('refresh')}
          >
            ⟳
          </button>
          <button
            type="button"
            className="kds-btn kds-btn-ghost"
            onClick={() => void toggleFullscreen()}
            title={t('fullScreen')}
            aria-label={t('fullScreen')}
          >
            ⛶
          </button>
          <button
            type="button"
            className="kds-btn kds-btn-ghost kds-hide-sm"
            onClick={onOpenServiceManager}
          >
            {t('serviceManager')}
          </button>
          <button type="button" className="kds-btn kds-btn-ghost" onClick={() => logout()}>
            {t('signOut')}
          </button>
        </div>
      </header>

      <nav className="kds-bar" aria-label={t('kds')}>
        <div className="kds-views" role="tablist">
          {VIEWS.map((view) => (
            <button
              key={view.mode}
              type="button"
              role="tab"
              aria-selected={viewMode === view.mode}
              className={`kds-tab ${viewMode === view.mode ? 'active' : ''}`}
              onClick={() => void setViewMode(view.mode)}
            >
              {t(view.key)}
            </button>
          ))}
        </div>

        {/* Station switcher, for the views that are scoped to one station. */}
        {(viewMode === 'station' || viewMode === 'allday' || viewMode === 'recall') && (
          <div className="kds-stations">
            {viewMode !== 'station' && (
              <button
                type="button"
                className={`kds-station-pill ${stationId == null ? 'active' : ''}`}
                onClick={() => void selectStation(null)}
              >
                {t('all')}
              </button>
            )}
            {stations
              .filter((s) => viewMode !== 'station' || s.type === 'prep')
              .map((s) => {
                const counts = store.stationCounts.get(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`kds-station-pill ${stationId === s.id ? 'active' : ''}`}
                    style={s.color ? ({ ['--pill']: s.color } as React.CSSProperties) : undefined}
                    onClick={() => void selectStation(s.id)}
                  >
                    <bdi>{lang === 'ar' ? s.nameAr || s.name : s.name}</bdi>
                    {counts && counts.open > 0 && (
                      <span className={`kds-pill-count ${counts.late > 0 ? 'late' : ''}`}>
                        {counts.open}
                      </span>
                    )}
                  </button>
                );
              })}
          </div>
        )}
      </nav>

      {viewMode !== 'allday' && (
        <BoardFilters
          filters={filters}
          options={filterOptions}
          presentOrderTypes={allTickets.map((ticket) => ticket.orderType)}
          onChange={setFilters}
        />
      )}

      {/* A filtered board is a board that is hiding work. Say so, in a place
          nobody can miss, with the number it is hiding. */}
      {isFiltering(filters) && hiddenCount > 0 && (
        <button type="button" className="kds-filter-banner" onClick={() => setFilters(EMPTY_FILTERS)}>
          {hiddenCount} {t('hidden')} — {t('clear')}
        </button>
      )}

      {error && <div className="kds-error">⚠ {error}</div>}

      {!ready && <div className="kds-empty">{t('loadingBoard')}</div>}

      {ready && viewMode === 'allday' && <AllDayBoard rows={store.allDay} />}

      {ready && viewMode !== 'allday' && list.length === 0 && (
        <div className="kds-empty">
          {viewMode === 'recall' ? t('nothingBumped') : t('nothingOnBoard')}
        </div>
      )}

      {ready && viewMode !== 'allday' && list.length > 0 && (
        <div
          className="kds-board"
          style={
            settings?.gridColumns
              ? ({ ['--cols']: String(settings.gridColumns) } as React.CSSProperties)
              : undefined
          }
        >
          {list.map((ticket) => {
            const ticketStation = stationById.get(ticket.stationId);
            return (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                settings={settings}
                warnSeconds={ticketStation?.warnSeconds ?? bands.warn}
                criticalSeconds={ticketStation?.criticalSeconds ?? bands.critical}
                skewMs={skewMs}
                pending={pending.has(ticket.id)}
                expo={expoView}
                showStation={spansStations}
                onStart={(id) => void store.start(id)}
                onBump={(id) => void store.bump(id)}
                onServe={requestServe}
                onRecall={(id) => void store.recall(id)}
                onPriority={(id, priority) => void store.setPriority(id, priority)}
                onHold={(id, held) => void store.setHold(id, held)}
                onFireCourse={(orderId, courseNo) => void store.fireCourse(orderId, courseNo)}
                onLine={(ticketId, lineId, isReady) =>
                  void store.setLine(ticketId, lineId, isReady ? 'ready' : 'preparing')
                }
                onPrint={(ticketId, kind) => void store.printTicket(ticketId, kind)}
                printing={store.printing}
              />
            );
          })}
        </div>
      )}

      {/* Resolved from the store's map rather than from the filtered list: a
          board that re-filters (or a ticket another station updates) under an
          open dialog must not make it vanish mid-decision. */}
      {serveTarget && (
        <ServeDialog
          ticket={serveTarget}
          onCancel={() => setServeTargetId(null)}
          onConfirm={(driverId) => {
            setServeTargetId(null);
            void store.serve(serveTarget.id, driverId);
          }}
        />
      )}

      <div className="kds-toasts" role="status" aria-live="polite">
        {store.toasts.map((toast) => (
          <button
            key={toast.id}
            type="button"
            className={`kds-toast ${toast.tone}`}
            onClick={() => store.dismissToast(toast.id)}
          >
            <span aria-hidden="true">{toast.tone === 'ok' ? '🖨' : '⚠'}</span>
            <span>
              <b>{t(toast.titleKey)}</b>
              {toast.detail && <em>{toast.detail}</em>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Which day's orders the board is showing.
 *
 * A kitchen screen is a live board, so the day it opens on is today and nothing
 * on the screen has to be touched to get that — the picker exists for the other
 * question, "what did we have on Friday", which somebody asks a few times a
 * week and currently cannot answer without a report.
 *
 * The value comes from the SERVER (`boardDate`), never from `new Date()` here.
 * Kitchen tablets are cheap and frequently minutes — sometimes days — out, and
 * a board that opened on the tablet's idea of today would show an empty screen
 * during service with no explanation. While the picker is off today the card
 * area is marked, because a cook glancing at an empty board must be able to see
 * WHY it is empty.
 */
function DayPicker(props: {
  date: string | null;
  today: string | null;
  onPick: (date: string) => void;
  t: (key: TKey) => string;
}): JSX.Element | null {
  const { date, today, onPick, t } = props;
  // Nothing to show until the first response has told us which day this is.
  if (!date) return null;
  const isToday = today != null && date === today;

  return (
    <div className={`kds-day ${isToday ? '' : 'kds-day--past'}`}>
      <input
        type="date"
        className="kds-day-input"
        value={date}
        // No `max`: a board for a future day is empty rather than wrong, and a
        // tenant whose timezone is ahead of the tablet's would otherwise find
        // today itself refused by the browser.
        onChange={(e) => {
          if (e.target.value) onPick(e.target.value);
        }}
        title={t('day')}
        aria-label={t('day')}
      />
      {!isToday && today != null && (
        <button type="button" className="kds-btn kds-btn-ghost" onClick={() => onPick(today)}>
          {t('today')}
        </button>
      )}
    </div>
  );
}

function Stat(props: { label: string; value: number; tone?: 'late' }): JSX.Element {
  return (
    <span className={`kds-stat ${props.tone ?? ''}`}>
      <b>{props.value}</b>
      <em>{props.label}</em>
    </span>
  );
}

/**
 * Connection state, always visible.
 *
 * A kitchen screen that has silently stopped receiving orders is the worst
 * failure this module has, and it looks exactly like a quiet evening. Showing
 * the transport state turns an invisible outage into something a manager can
 * see from across the pass.
 */
function ConnBadge(props: { conn: string }): JSX.Element {
  const { t } = useI18n();
  const label =
    props.conn === 'live'
      ? t('live')
      : props.conn === 'polling'
        ? t('polling')
        : props.conn === 'degraded'
          ? t('reconnecting')
          : t('connecting');
  return (
    <span className={`kds-conn conn-${props.conn}`} title={label}>
      <i aria-hidden="true" />
      {label}
    </span>
  );
}

async function toggleFullscreen(): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen();
  } catch {
    // Refused (iOS Safari has no Fullscreen API on iPhone). The button simply
    // does nothing rather than throwing at the cook.
  }
}
